import { Accessor, createEffect, createSignal, Setter } from 'solid-js';
import { queryResults as api, ColumnMetadata, QueryResult } from 'st-app/api'

export class QueryResultsContext {
    query?: string;
    data?: Promise<QueryResult>;

    columnMetadata: Accessor<ColumnMetadata[]>; setColumnMetadata: Setter<ColumnMetadata[]>;
    loading: Accessor<boolean>; setLoading: Setter<boolean>;
    sqlStatement: Accessor<string>; setSqlStatement: Setter<string>;
    noDataFound: Accessor<boolean>; setNoDataFound: Setter<boolean>;
    errorMessage: Accessor<string>; setErrorMessage: Setter<string>;
    store: DevExpress.data.CustomStore<unknown, unknown>;
    pivotDataSource: DevExpress.data.PivotGridDataSource;

    constructor() {
        [this.loading, this.setLoading] = createSignal(false);
        [this.columnMetadata, this.setColumnMetadata] = createSignal<ColumnMetadata[]>([]);
        [this.sqlStatement, this.setSqlStatement] = createSignal<string>('');
        [this.noDataFound, this.setNoDataFound] = createSignal(false);
        [this.errorMessage, this.setErrorMessage] = createSignal<string>('');

        this.store = new DevExpress.data.CustomStore({
            load: async () => {
                const results = await this.load(this.query);
                return results ? results.rows : [];
            },
        });

        this.pivotDataSource = new DevExpress.data.PivotGridDataSource({
            store: this.store,
            onFieldsPrepared: (fields) => {
                for (const metadata of this.columnMetadata()) {
                    const field = fields.find(f => f.dataField === metadata.columnName);
                    if (field) {
                        field.caption = metadata.columnLabel;
                    }
                }
            }
        });
    }

    async load(query?: string) {
        if (this.query === query)
            return this.data;

        try {
            this.setLoading(true);
            this.setNoDataFound(false);
            this.setErrorMessage('');
            this.query = query;
            this.data = executeQuery(this.query).then(result => {
                this.setColumnMetadata(result.columnMetadata);
                this.setSqlStatement(result.sqlStatement ?? '');
                this.setNoDataFound(result.rows.length === 0);
                this.setErrorMessage(result.errorMessage ?? '');
                return result;
            });
            return await this.data;
        } catch (err) {
            // Reset so the query can be retried
            this.query = undefined;
            this.data = undefined;
            throw err;
        } finally {
            this.setLoading(false);
        }
    }
}


async function executeQuery(query?: string): Promise<QueryResult> {
    if (!query) {
        return {
            columnMetadata: [],
            rows: [],
        };
    }
    
    if (import.meta.env.DEV) {
        if (localStorage.getItem('lastQuery') === query) {
            const localstorageData = localStorage.getItem('queryResults');
            if (localstorageData) {
                return JSON.parse(localstorageData) as QueryResult;
            }
        }
    }

    const data = await api.GetResults(query);

    if (import.meta.env.DEV) {
        localStorage.setItem('lastQuery', query);
        localStorage.setItem('queryResults', JSON.stringify(data));
    }

    return data;
}